// Voice Mode 2.0 — P1: the React glue for a live voice session.
//
// start() → open a server session → connect the WS → browser STT (free) sends
// user text → played PCM comes back → energy VAD notices barge-in and cuts the
// audio. Every external dependency is injectable, so the whole glue is testable
// without a mic, a model or a socket.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  voiceApi,
  VoiceConfig,
  VoiceSessionInfo,
  VoiceSessionMode,
  VoiceSessionSummary,
  VoiceSessionsResponse,
} from '../../api/voice';
import { VoiceLiveClient, SocketFactory, SpokenSegment } from './client';
import { AudioContextFactory, PcmPlayer } from './player';
import { SpeechTranscriptor, RecognitionFactory, defaultRecognitionFactory } from './stt';
import { VoiceActivityDetector } from './vad';

export type VoiceLiveStatus = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error';

export interface VoiceLiveDeps {
  createSession: (workspaceId: string, mode?: VoiceSessionMode | string, conversationId?: string, surface?: string) => Promise<VoiceSessionInfo>;
  endSession: (sessionId: string, reason?: string) => Promise<unknown>;
  endAllSessions: () => Promise<unknown>;
  /** Live sessions this user owns (GET /voice/sessions) — the resume-on-mount probe. */
  listSessions: () => Promise<VoiceSessionsResponse>;
  /** Voice config — fills sample rate / endpointing for a re-attached session. */
  voiceConfig: () => Promise<VoiceConfig>;
  socketFactory?: SocketFactory;
  contextFactory?: AudioContextFactory;
  recognitionFactory?: RecognitionFactory;
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  getToken: () => string | null;
}

export interface UseVoiceLiveOptions {
  workspaceId: string | null;
  deps?: Partial<VoiceLiveDeps>;
  /** UI tick rate for the level meter (ms). */
  meterIntervalMs?: number;
  /**
   * Session mode. Defaults to the existing "standard" cascade loop; pass
   * "spoken_thoughts" to opt into the additive labelled-reasoning mode (needs
   * its own server flag). Purely additive — omitting it changes nothing.
   */
  mode?: VoiceSessionMode | string;
  /**
   * Owning chat conversation. When set, live turns route through that
   * conversation's bridge — e.g. the OpenClaw chat — so live talk works there
   * too (owner report 2026-10-01). Omit for the default daily session.
   */
  conversationId?: string | null;
  /**
   * Which surface opened the session. "widget" enables on-screen navigation
   * (the server blocks navigation for "chat", since leaving the chat ends the
   * conversation). Omit to keep the server default ("chat").
   */
  surface?: string;
}

const DEFAULT_DEPS: Omit<VoiceLiveDeps, 'socketFactory' | 'contextFactory' | 'recognitionFactory'> = {
  createSession: (workspaceId: string, mode?: VoiceSessionMode | string, conversationId?: string, surface?: string) =>
    voiceApi.createSession(workspaceId, mode, conversationId, surface),
  endSession: (sessionId: string, reason?: string) => voiceApi.endSession(sessionId, reason),
  endAllSessions: () => voiceApi.endAllSessions(),
  listSessions: () => voiceApi.sessions(),
  voiceConfig: () => voiceApi.config(),
  getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
  getToken: () => localStorage.getItem('saik_token'),
};

/** Friendly text for the failure modes a phone/browser actually hits. */
export function describeVoiceError(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  if (status === 429) {
    return 'Voice session limit reached. Close the other voice tab (or wait a moment) and try again.';
  }
  if (status === 403) {
    return 'Live voice is not available for this account yet.';
  }
  if (typeof detail === 'string' && detail) return detail;
  return error instanceof Error ? error.message : 'Could not start live voice';
}

/**
 * True when two transcripts are the same spoken utterance.  Chrome finalises one
 * phrase several times, sometimes with an extra word, and each duplicate used to
 * start a new turn that cut the reply that was still playing.
 */
export function sameUtterance(a: string, b: string): boolean {
  const norm = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

/**
 * True when a transcript is (part of) what the assistant itself just said.
 *
 * The microphone hears the reply coming out of the speaker and Chrome
 * transcribes it; without this the agent answers its own voice — the
 * "responding to itself" behaviour reported on 2026-09-20.
 */
export function isEcho(transcript: string, spoken: string): boolean {
  const norm = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const said = norm(spoken);
  const heard = norm(transcript);
  if (!said || !heard) return false;
  if (heard.length < 4) return false;
  if (said.includes(heard)) return true;
  const saidWords = new Set(said.split(' '));
  const heardWords = heard.split(' ');
  if (heardWords.length < 2) return false;
  const overlap = heardWords.filter((word) => saidWords.has(word)).length;
  return overlap / heardWords.length >= 0.6;
}

export type FinalDecision = 'accept' | 'echo' | 'suppress' | 'duplicate' | 'stale';

/**
 * Decide what to do with a final transcript. Pure so the rules are testable.
 *
 * Order matters: audio gating first (the mic was hearing the assistant), then
 * echo matching, then the duplicate/stale guards.
 *
 * VAD INDEPENDENCE (owner report 2026-09-21): the energy VAD is a *hint*, not
 * an authority.  A short, quiet, or clipped command ("open tasks", "trading")
 * often never trips it, and the old rules then threw the transcript away as
 * 'stale'/'suppress' — so commands under ~5 words silently did nothing while a
 * long sentence worked.  Web Speech has already decided this is a FINAL, real
 * transcript.  We only let the VAD gate us while the assistant is genuinely
 * making sound (playbackActive / deaf window); otherwise a fresh, non-echo,
 * non-duplicate transcript is accepted.
 */
export function decideFinalTranscript(input: {
  text: string;
  now: number;
  playbackActive: boolean;
  suppressUntil: number;
  spoken: string;
  last: { text: string; at: number };
  turnActive: boolean;
  spokeSinceTurn: boolean;
  awaitingSpeech: boolean;
  duplicateWindowMs?: number;
}): FinalDecision {
  const text = input.text.trim();
  if (text.length < 2) return 'suppress';
  // Deaf while our own audio is audible, plus a short tail after it stops.
  if (input.playbackActive || input.now < input.suppressUntil) return 'suppress';
  if (isEcho(text, input.spoken)) return 'echo';
  const window = input.duplicateWindowMs ?? 8000;
  if (sameUtterance(text, input.last.text) && input.now - input.last.at < window) return 'duplicate';
  return 'accept';
}

/**
 * Open a session, recovering automatically from the stale-session 429: close
 * every live session this user owns and try once more.
 */
async function openSessionWithRecovery(
  deps: VoiceLiveDeps,
  workspaceId: string,
  mode?: VoiceSessionMode | string,
  conversationId?: string | null,
  surface?: string,
): Promise<VoiceSessionInfo> {
  // Only send `mode` when one was asked for: a plain start() keeps the exact
  // one-argument call shape it had before the mode option existed.
  // `surface` is only sent when set, so existing callers are unchanged.
  const create = () =>
    mode === undefined || mode === null
      ? (surface ? deps.createSession(workspaceId, undefined, conversationId || undefined, surface)
                 : deps.createSession(workspaceId, undefined, conversationId || undefined))
      : (surface ? deps.createSession(workspaceId, mode, conversationId || undefined, surface)
                 : deps.createSession(workspaceId, mode, conversationId || undefined));
  try {
    return await create();
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status !== 429) throw error;
    await deps.endAllSessions().catch(() => undefined);
    return await create();
  }
}

/**
 * Did the user leave live voice ON in this tab?
 *
 * The hook tears the session down when the page unmounts, so a remount cannot
 * know from React state whether the owner was mid-conversation.  A
 * `sessionStorage` flag (per tab, gone when the tab closes) records the
 * INTENT, and is what stops a plain visit to the Chat page from opening the
 * microphone by itself.
 */
const LIVE_INTENT_KEY = 'saik_live_voice_intent';

export function setLiveIntent(on: boolean): void {
  try {
    if (on) window.sessionStorage.setItem(LIVE_INTENT_KEY, '1');
    else window.sessionStorage.removeItem(LIVE_INTENT_KEY);
  } catch {
    /* private mode / storage disabled: resume simply stays off */
  }
}

export function liveIntent(): boolean {
  try {
    return window.sessionStorage.getItem(LIVE_INTENT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Only ONE mounted consumer may resume a given session.
 *
 * Two components mount this hook at the same time (the Chat page and the global
 * AssistantWidget), so on a full page load both would otherwise race to
 * re-attach to the same server-side session — two sockets, two microphones.
 * The first claim wins; a later visit (outside the window) may claim again.
 */
let lastResumeClaim: { sessionId: string; at: number } = { sessionId: '', at: 0 };
const RESUME_CLAIM_WINDOW_MS = 15_000;

export function claimResume(sessionId: string): boolean {
  const now = Date.now();
  if (lastResumeClaim.sessionId === sessionId && now - lastResumeClaim.at < RESUME_CLAIM_WINDOW_MS) {
    return false;
  }
  lastResumeClaim = { sessionId, at: now };
  return true;
}

/**
 * Only auto-reopen the microphone when the browser already granted it.
 *
 * A page-load resume has no user gesture behind it, so on a browser that has
 * never been asked (or was denied) we stay quiet and let the owner tap the
 * button — that tap is what may raise the one-and-only permission prompt.
 */
export async function microphoneAlreadyGranted(): Promise<boolean> {
  try {
    const perms = (
      navigator as unknown as {
        permissions?: { query: (descriptor: unknown) => Promise<{ state: string }> };
      }
    ).permissions;
    if (!perms?.query) return true; // no Permissions API (Firefox/Safari): assume yes
    const status = await perms.query({ name: 'microphone' });
    return status.state === 'granted';
  } catch {
    return true; // unsupported descriptor — do not block the resume
  }
}

/**
 * Rebuild a full session descriptor from a `GET /voice/sessions` summary.
 *
 * The summary carries the id/state but not the transport knobs, so the values
 * come from `/voice/config` (the same settings the create-session response
 * would have returned).
 */
export async function sessionInfoFromLive(
  deps: VoiceLiveDeps,
  live: VoiceSessionSummary,
): Promise<VoiceSessionInfo> {
  let cfg: VoiceConfig | null = null;
  try {
    cfg = await deps.voiceConfig();
  } catch {
    cfg = null; // fall back to the same defaults start() uses
  }
  return {
    session_id: live.session_id,
    mode: live.mode,
    state: live.state,
    ws_path: `/api/v1/voice/session/${live.session_id}/ws`,
    sample_rate: cfg?.sample_rate ?? null,
    format: cfg?.format ?? 'int16',
    channels: cfg?.channels ?? 1,
    endpointing_ms: cfg?.endpointing_ms ?? 500,
    max_seconds: cfg?.max_seconds ?? 900,
    silence_seconds: cfg?.silence_seconds ?? 90,
  };
}

export function useVoiceLive({ workspaceId, deps, meterIntervalMs = 60, mode, conversationId, surface }: UseVoiceLiveOptions) {
  const [status, setStatus] = useState<VoiceLiveStatus>('idle');
  // The full reply text, accumulated.  A caption is a transcript of what was
  // said, so it must GROW with the reply rather than show only its last
  // sentence (2026-09-20: the owner heard sentence 1 but the widget showed
  // sentence 2, so "what I was reading wasn't what was said").
  const [caption, setCaption] = useState('');
  const [interim, setInterim] = useState('');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<VoiceSessionInfo | null>(null);
  const [lastLatencyMs, setLastLatencyMs] = useState<number | null>(null);
  // Spoken thoughts: the labelled utterances and their pending/speaking/spoken/
  // skipped state (spoken-thoughts mode only; empty in the classic loop).
  const [thoughts, setThoughts] = useState<SpokenSegment[]>([]);

  const clientRef = useRef<VoiceLiveClient | null>(null);
  const playerRef = useRef<PcmPlayer | null>(null);
  const sttRef = useRef<SpeechTranscriptor | null>(null);
  const vadRef = useRef<VoiceActivityDetector | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);
  const activeRef = useRef(false);
  // One in-flight turn at a time: Chrome fires several final transcripts for a
  // single utterance (and again on trailing silence), which used to start a new
  // turn and cut off the reply that was still playing.
  const turnActiveRef = useRef(false);
  const lastSentRef = useRef<{ text: string; at: number }>({ text: '', at: 0 });
  // Did the microphone pick up *new* speech since the current turn started?
  // Without this, a trailing final from the utterance that already triggered the
  // turn looks like a fresh question and cuts the reply short.
  const spokeSinceTurnRef = useRef(false);
  // What the assistant has said in the current reply — used to recognise its own
  // voice coming back through the microphone.
  const spokenRef = useRef('');
  // Playback (and its echo tail) is deaf territory: no transcript is accepted
  // before this timestamp.
  const suppressUntilRef = useRef(0);
  // After a barge-in we wait for genuinely new speech before starting a turn,
  // otherwise the cut-off tail becomes a fresh question.
  const awaitingSpeechRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const statusRef = useRef<VoiceLiveStatus>('idle');
  statusRef.current = status;
  // One-shot gesture handler that unlocks a resumed session's AudioContext
  // (a mount-time resume has no click of its own).
  const unlockRef = useRef<(() => void) | null>(null);
  // Return-to-page resume runs at most once per mount.
  const autoResumeRef = useRef<'idle' | 'running' | 'done'>('idle');

  const depsRef = useRef({ ...DEFAULT_DEPS, ...deps });
  depsRef.current = { ...DEFAULT_DEPS, ...deps };
  const conversationIdRef = useRef<string | null>(conversationId ?? null);
  conversationIdRef.current = conversationId ?? null;
  // The conversation the server has actually ACKed for this session; if the
  // viewed chat differs we re-sync it (covers a frame dropped while connecting).
  const serverConvRef = useRef<string | null>(null);

  const teardown = useCallback(() => {
    activeRef.current = false;
    if (unlockRef.current) {
      window.removeEventListener('pointerdown', unlockRef.current);
      window.removeEventListener('keydown', unlockRef.current);
      unlockRef.current = null;
    }
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    sttRef.current?.stop();
    sttRef.current = null;
    clientRef.current?.close();
    clientRef.current = null;
    void playerRef.current?.dispose();
    playerRef.current = null;
    analyserRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    void ctx?.close?.().catch(() => undefined);
    vadRef.current = null;
    setInterim('');
    setLevel(0);
    setThoughts([]);
  }, []);

  const stop = useCallback(
    (reason = 'user') => {
      const sessionId = session?.session_id;
      if (sessionId) void depsRef.current.endSession(sessionId, reason).catch(() => undefined);
      // An explicit stop is not "come back to this later".
      setLiveIntent(false);
      teardown();
      setStatus('idle');
      setSession(null);
    },
    [session?.session_id, teardown],
  );

  useEffect(() => () => teardown(), [teardown]);

  // ONE wiring path for a brand-new session AND for a re-attached one: WS +
  // browser STT + mic + level meter.  Extracted from start() so the
  // return-to-page resume can re-open the same pipeline (2026-09-30).
  const wireSession = useCallback(
    async (created: VoiceSessionInfo, earlyPlayer: PcmPlayer) => {
      const sampleRate = created.sample_rate ?? 22050;

      // The context was already created and unlocked by the click above; keep it
      // when the sample rate matches so the unlock is not wasted.
      const player = earlyPlayer;
      player.sampleRate = sampleRate;
      playerRef.current = player;
      // Unlock again (cheap, idempotent) without awaiting: on iOS the promise
      // only settles once the context is genuinely running, and if it hangs
      // (interruption / policy) awaiting it here would stall the whole chain —
      // session open, mic never started, nothing ever sent (owner report
      // 2026-09-21: "it isn't picking up anything I'm saying").  Capture and
      // recognition do not depend on playback being unlocked.
      void player.resume();

      const vad = new VoiceActivityDetector({
        endpointingMs: created.endpointing_ms,
        bargeInMs: 200,
        sampleRate: 48000,
      });
      vadRef.current = vad;

      const client = new VoiceLiveClient({
        sessionId: created.session_id,
        token: depsRef.current.getToken() ?? '',
        socketFactory: depsRef.current.socketFactory,
        onEvent: (event) => {
          switch (event.type) {
            case 'ready':
              setStatus('listening');
              break;
            case 'state':
              // Never stop playback on a state frame: the server reports
              // 'listening' as soon as the reply has been *queued*, which is
              // still seconds before it has finished playing. Barge-in is the
              // only thing that stops audio.
              if (event.state === 'thinking') setStatus('thinking');
              else if (event.state === 'speaking') setStatus('speaking');
              else if (event.state === 'listening' && !playerRef.current?.playing) setStatus('listening');
              break;
            case 'sentence':
              // Append: the caption is the transcript of the whole reply.
              setCaption((prev) => (prev ? `${prev} ${event.text}` : event.text));
              spokenRef.current = `${spokenRef.current} ${event.text}`.slice(-800);
              break;
            case 'first_audio':
              setLastLatencyMs(event.latency_ms ?? null);
              setStatus('speaking');
              break;
            case 'interrupted':
              playerRef.current?.stop();
              turnActiveRef.current = false;
              spokeSinceTurnRef.current = false;
              awaitingSpeechRef.current = true;
              suppressUntilRef.current = Date.now() + 900;
              sttRef.current?.flush();
              setStatus('listening');
              break;
            case 'turn_end':
              turnActiveRef.current = false;
              if (event.first_audio_latency_ms != null) setLastLatencyMs(event.first_audio_latency_ms);
              if (!playerRef.current?.playing) setStatus('listening');
              // The server wrote this turn into the active chat; refresh it.
              try { window.dispatchEvent(new CustomEvent('saik:voice-turn')); } catch { /* ignore */ }
              break;
            case 'spoken_state':
              setThoughts(event.segments ?? []);
              break;
            case 'conversation_ack':
              serverConvRef.current = event.conversation_id ?? null;
              break;
            case 'ended':
              teardown();
              setStatus('idle');
              setSession(null);
              setThoughts([]);
              setLiveIntent(false);
              break;
            case 'error':
              // Surface the failure in the UI *and* the browser console so a
              // silent failure (e.g. a transcript persist error) is catchable
              // instead of the session just appearing to stop (owner 2026-10-02).
              console.error('[voice] server error:', event.detail);
              setError(event.detail);
              break;
            default:
              break;
          }
        },
        onAudio: (pcm) => {
          player.enqueue(pcm);
          vad.setPlaybackActive(true);
        },
        onClose: (reason) => {
          if (activeRef.current && reason !== 'closed') {
            setError('Voice connection lost');
            teardown();
            setStatus('error');
            setLiveIntent(false);
          }
        },
      });
      clientRef.current = client;
      client.connect();

      // MIC FIRST, then Web Speech (2026-09-30, owner: "it asks twice").
      // Web Speech recognition asks for the microphone on its own; with
      // getUserMedia racing it in the same tick the browser pops TWO
      // permission prompts.  One request here, awaited, and the recogniser
      // that starts afterwards inherits the granted permission — exactly one
      // prompt per session start.
      const stream = await depsRef.current.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const transcriptor = new SpeechTranscriptor({
        factory: depsRef.current.recognitionFactory,
        onFinal: (text) => {
          const trimmed = text.trim();
          setInterim('');
          const decision = decideFinalTranscript({
            text: trimmed,
            now: Date.now(),
            playbackActive: Boolean(playerRef.current?.playing),
            suppressUntil: suppressUntilRef.current,
            spoken: spokenRef.current,
            last: lastSentRef.current,
            turnActive: turnActiveRef.current,
            spokeSinceTurn: spokeSinceTurnRef.current,
            awaitingSpeech: awaitingSpeechRef.current,
          });
          if (decision !== 'accept') return; // echo of our own voice, duplicate, or stale tail
          lastSentRef.current = { text: trimmed, at: Date.now() };
          turnActiveRef.current = true;
          spokeSinceTurnRef.current = false;
          awaitingSpeechRef.current = false;
          spokenRef.current = '';
          // New turn → new transcript.  Without this the previous answer would
          // still be on screen and the caption would grow across turns.
          setCaption('');
          // Reset the per-turn first-audio signal. The widget uses
          // lastLatencyMs to decide whether the server voiced this turn (and so
          // must NOT device-speak on top of it). Leaving it sticky would make a
          // silent turn look voiced, or vice-versa, across turns.
          setLastLatencyMs(null);
          setStatus('thinking');
          clientRef.current?.sendUserText(trimmed);
        },
        onInterim: setInterim,
        onError: (code) => {
          if (code !== 'unsupported') setError(`Microphone: ${code}`);
        },
      });
      sttRef.current = transcriptor;
      if (!transcriptor.start()) {
        setError('Speech recognition is not available in this browser');
      }

      const AudioCtor =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AudioCtor) {
        const ctx = new AudioCtor();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);
        analyserRef.current = analyser;
        const frame = new Float32Array(analyser.fftSize);

        timerRef.current = window.setInterval(() => {
          if (!activeRef.current || !analyserRef.current) return;
          analyserRef.current.getFloatTimeDomainData(frame);
          const signal = vad.feed(frame);
          setLevel(vad.level);
          if (signal === 'speech_start') {
            spokeSinceTurnRef.current = true;
            awaitingSpeechRef.current = false;
          }
          const playing = Boolean(playerRef.current?.playing);
          // The moment playback ends, keep a short deaf window: the speaker's
          // last word is still in the room and would otherwise be transcribed.
          if (wasPlayingRef.current && !playing) {
            suppressUntilRef.current = Math.max(suppressUntilRef.current, Date.now() + 1200);
          }
          wasPlayingRef.current = playing;
          vad.setPlaybackActive(playing);
          // Playback finished while we were still 'speaking' → back to listening.
          if (!playing && statusRef.current === 'speaking') setStatus('listening');
          if (signal === 'barge_in') {
            playerRef.current?.stop();
            clientRef.current?.bargeIn();
            sttRef.current?.flush();
            turnActiveRef.current = false;
            spokeSinceTurnRef.current = false;
            awaitingSpeechRef.current = true;
            suppressUntilRef.current = Date.now() + 900;
          }
        }, meterIntervalMs);
      }
    },
    [meterIntervalMs, teardown],
  );

  /**
   * Open a live session and wire it up.
   *
   * `attachTo` re-attaches to a session the server still lists (return-to-page
   * resume); otherwise a brand-new session is created.
   */
  const begin = useCallback(
    async (attachTo?: VoiceSessionSummary | null) => {
      if (activeRef.current || !workspaceId) return;
      activeRef.current = true;
      setError(null);
      setStatus('connecting');
      setCaption('');

      // Create the audio context SYNCHRONOUSLY, inside the user's click, and ask it
      // to resume now.  Browsers only honour the unlock while the gesture is still
      // "active", and awaiting the network first (below) loses it — a suspended
      // context means the reply is silent and the widget can look deaf.  The
      // promise is deliberately not awaited: a context that refuses to start must
      // never block capture or recognition.
      const earlyPlayer = new PcmPlayer({
        sampleRate: 22050,
        contextFactory: depsRef.current.contextFactory,
      });
      playerRef.current = earlyPlayer;
      void earlyPlayer.resume();

      // A session woken up on mount has no click of its own, so on a hard reload
      // the context can start suspended (silent replies).  The first interaction
      // anywhere on the page unlocks it; teardown removes the handler.
      const unlockPlayer = () => {
        void playerRef.current?.resume();
      };
      unlockRef.current = unlockPlayer;
      window.addEventListener('pointerdown', unlockPlayer, { once: true });
      window.addEventListener('keydown', unlockPlayer, { once: true });

      // Refuse early when the browser has no speech recognition.  Creating a
      // session first would leave an orphan holding the per-user slot (exactly
      // how the 429s started on 2026-09-20).
      const recognition = depsRef.current.recognitionFactory ?? defaultRecognitionFactory;
      let recognitionSupported = false;
      try {
        recognitionSupported = recognition() !== null;
      } catch {
        recognitionSupported = false;
      }
      if (!recognitionSupported) {
        activeRef.current = false;
        setStatus('error');
        setError('Live voice needs a browser with speech recognition (Chrome on desktop or Android).');
        void earlyPlayer.dispose();
        playerRef.current = null;
        return;
      }

      try {
        const created = attachTo
          ? await sessionInfoFromLive(depsRef.current, attachTo)
          : await openSessionWithRecovery(depsRef.current, workspaceId, mode, conversationId, surface);
        setSession(created);
        setLiveIntent(true);
        await wireSession(created, earlyPlayer);
      } catch (err) {
        teardown();
        setStatus('error');
        setError(describeVoiceError(err));
        setLiveIntent(false);
      }
    },
    [mode, teardown, wireSession, workspaceId, conversationId, surface],
  );

  const start = useCallback(async () => {
    await begin(null);
  }, [begin]);

  // ── Return-to-page resume (owner report 2026-09-30) ────────────────────
  // The hook tears the live session down on unmount, so navigating away and
  // back used to leave live voice dead (and, while the server still listed the
  // session, the next start could be refused).  On mount, if the owner had
  // live voice ON in this tab, re-open the stream by re-attaching to a session
  // the server still lists; when there is none, do nothing.  Runs once per
  // mount, and only when the microphone was ALREADY granted — a page load must
  // never raise a permission prompt of its own.
  useEffect(() => {
    if (!workspaceId || !liveIntent()) return;
    if (autoResumeRef.current !== 'idle') return;
    autoResumeRef.current = 'running';
    let cancelled = false;
    const run = async () => {
      try {
        if (!(await microphoneAlreadyGranted())) {
          if (!cancelled) setLiveIntent(false);
          return;
        }
        let live: VoiceSessionSummary[] = [];
        try {
          live = (await depsRef.current.listSessions())?.live ?? [];
        } catch {
          live = []; // voice disabled / offline: stay quiet, the button still works
        }
        if (cancelled || activeRef.current) return;
        const existing = live.find(
          (s) => !s.reason && (!s.workspace_id || s.workspace_id === workspaceId),
        );
        if (!existing) return; // nothing live to resume: do nothing
        // The Chat page and the AssistantWidget both mount this hook: exactly
        // one of them may re-attach to a given session.
        if (!claimResume(existing.session_id)) return;
        await begin(existing);
      } finally {
        if (autoResumeRef.current === 'running') autoResumeRef.current = 'done';
      }
    };
    void run();
    return () => {
      cancelled = true;
      // A StrictMode throwaway mount must not consume the once-per-mount guard.
      if (autoResumeRef.current === 'running') autoResumeRef.current = 'idle';
    };
  }, [begin, workspaceId]);

  const toggle = useCallback(() => {
    if (activeRef.current) stop();
    else void start();
  }, [start, stop]);

  const sendText = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    clientRef.current?.sendUserText(trimmed, conversationIdRef.current);
    setLastLatencyMs(null);
    setStatus('thinking');
  }, []);

  // Keep the live session pointed at the chat the user is actually looking at.
  useEffect(() => {
    conversationIdRef.current = conversationId ?? null;
    clientRef.current?.setConversation(conversationId ?? null);
    serverConvRef.current = conversationId ?? null;
  }, [conversationId]);

  // If a live session was opened AFTER this hook mounted (e.g. via a ring), the
  // effect above already ran with a null id, so the new session was never told
  // which chat to talk to — re-sync once a session appears (owner 2026-10-02:
  // "start on the agent then switch to OpenClaw → doesn't work without relaunch").
  useEffect(() => {
    if (!session) return;
    const want = conversationIdRef.current;
    if (serverConvRef.current !== want) {
      clientRef.current?.setConversation(want ?? null);
    }
  }, [session]);

  // Answering an in-app ring should start the live conversation, not just open
  // the chat (owner 2026-10-02).  The ring overlay dispatches 'saik:start-live'.
  //
  // Race fix (owner 2026-10-10: "it didn't auto turn on after I picked up the
  // call"): the widget + the ring bar can both dispatch this, and one of them
  // may fire the event while this hook is unmounted/remounting (a route change
  // tears the widget's hook down and back up).  We now (a) start if not already
  // active and (b) remember the request for a few seconds so a hook that mounts
  // just after the event still honours it.  We also start directly on the
  // ring-answer intent rather than relying on any single listener being alive.
  useEffect(() => {
    const handler = () => {
      (window as unknown as { __saikLiveRequestAt?: number }).__saikLiveRequestAt = Date.now();
      if (!activeRef.current) void start();
    };
    window.addEventListener('saik:start-live', handler);
    // Honour a request that arrived just before this hook mounted.
    const justAsked = Date.now() - ((window as unknown as { __saikLiveRequestAt?: number }).__saikLiveRequestAt || 0);
    if (!activeRef.current && justAsked <= 3000) void start();
    return () => window.removeEventListener('saik:start-live', handler);
  }, [start]);
  const bargeIn = useCallback(() => {
    playerRef.current?.stop();
    clientRef.current?.bargeIn();
  }, []);

  return {
    status,
    caption,
    interim,
    level,
    error,
    session,
    lastLatencyMs,
    thoughts,
    active: status !== 'idle',
    start,
    stop,
    toggle,
    sendText,
    bargeIn,
  };
}

export default useVoiceLive;
